import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import { 
  collection, 
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  orderBy,
  serverTimestamp,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { INITIAL_DEMO_FAMILY } from '../lib/demoData';

const FamilyContext = createContext();

export const useFamily = () => {
  const context = useContext(FamilyContext);
  if (!context) {
    throw new Error('useFamily must be used within a FamilyProvider');
  }
  return context;
};

export const ROLES = {
  OWNER: 'owner',
  ADMIN: 'admin',
  MEMBER: 'member'
};

// Family currency display modes
export const CURRENCY_DISPLAY_MODES = {
  BASE_CURRENCY: 'base_currency',     // Everyone sees amounts in family's base currency
  MEMBER_CURRENCY: 'member_currency'  // Each member sees in their own currency
};

export const FamilyProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [family, setFamily] = useState(null);
  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [sharedTransactions, setSharedTransactions] = useState([]);
  const [sharedBudgets, setSharedBudgets] = useState({});
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [unsubscribers, setUnsubscribers] = useState([]);
  const [familySettings, setFamilySettings] = useState({
    baseCurrency: 'INR',
    currencyDisplayMode: CURRENCY_DISPLAY_MODES.MEMBER_CURRENCY
  });
  const [familyNotifications, setFamilyNotifications] = useState([]);

  // Cleanup function
  const cleanupSubscriptions = () => {
    unsubscribers.forEach(unsub => {
      if (typeof unsub === 'function') unsub();
    });
    setUnsubscribers([]);
  };

  // Listen for user's family membership
  useEffect(() => {
    if (!currentUser) {
      setFamily(null);
      setMembers([]);
      setLoading(false);
      return;
    }

    if (currentUser.isGuest) {
      setFamily(INITIAL_DEMO_FAMILY);
      setUserRole(ROLES.OWNER);
      setMembers([
        {
          userId: 'demo_guest_user',
          displayName: 'Demo User (You)',
          email: 'demo@spendeasy.app',
          role: ROLES.OWNER
        },
        {
          userId: 'member_2',
          displayName: 'Alex Smith',
          email: 'alex@example.com',
          role: ROLES.MEMBER
        }
      ]);
      setSharedTransactions([
        {
          id: 'shared-tx-1',
          type: 'expense',
          amount: 3200,
          category: 'Groceries',
          categoryIcon: '🛒',
          userId: 'demo_guest_user',
          userName: 'Demo User',
          description: 'Weekly bulk groceries',
          date: new Date()
        },
        {
          id: 'shared-tx-2',
          type: 'expense',
          amount: 1200,
          category: 'Utilities',
          categoryIcon: '💡',
          userId: 'member_2',
          userName: 'Alex Smith',
          description: 'High-speed internet bill',
          date: new Date()
        }
      ]);
      setSharedBudgets({
        'Groceries': 15000,
        'Utilities': 6000,
        'Rent/Mortgage': 25000
      });
      setLoading(false);
      return;
    }

    // Check if user is part of any family
    const checkFamilyMembership = async () => {
      try {
        // Check as owner
        const ownerQuery = query(
          collection(db, 'families'),
          where('ownerId', '==', currentUser.uid)
        );
        const ownerSnapshot = await getDocs(ownerQuery);
        
        if (!ownerSnapshot.empty) {
          const familyDoc = ownerSnapshot.docs[0];
          subscribeToFamily(familyDoc.id);
          return;
        }

        // Check as member
        const memberQuery = query(
          collection(db, 'families'),
          where('memberIds', 'array-contains', currentUser.uid)
        );
        const memberSnapshot = await getDocs(memberQuery);
        
        if (!memberSnapshot.empty) {
          const familyDoc = memberSnapshot.docs[0];
          subscribeToFamily(familyDoc.id);
          return;
        }

        // Check pending invites
        const inviteQuery = query(
          collection(db, 'familyInvites'),
          where('email', '==', currentUser.email),
          where('status', '==', 'pending')
        );
        const inviteSnapshot = await getDocs(inviteQuery);
        
        if (!inviteSnapshot.empty) {
          setPendingInvites(inviteSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })));
        }

        setLoading(false);
      } catch (error) {
        console.error('Error checking family membership:', error);
        setLoading(false);
      }
    };

    checkFamilyMembership();
    return () => cleanupSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const subscribeToFamily = (familyId) => {
    // Clean up any existing subscriptions
    cleanupSubscriptions();
    
    const newUnsubscribers = [];
    
    // Subscribe to family document
    const familyRef = doc(db, 'families', familyId);
    const unsubFamily = onSnapshot(familyRef, async (snapshot) => {
      if (snapshot.exists()) {
        const familyData = { id: snapshot.id, ...snapshot.data() };
        setFamily(familyData);
        
        // Determine user role
        if (familyData.ownerId === currentUser.uid) {
          setUserRole(ROLES.OWNER);
        } else {
          const memberRole = familyData.memberRoles?.[currentUser.uid] || ROLES.MEMBER;
          setUserRole(memberRole);
        }

        // Fetch member details (prefer embedded meta to avoid cross-user read rules)
        const memberDetails = await fetchMemberDetails(familyData);
        setMembers(memberDetails);
      } else {
        // Family was deleted
        setFamily(null);
        setMembers([]);
        setUserRole(null);
      }
      setLoading(false);
    });
    newUnsubscribers.push(unsubFamily);

    // Subscribe to shared transactions
    const transRef = collection(db, 'families', familyId, 'sharedTransactions');
    const unsubTrans = onSnapshot(query(transRef), (snapshot) => {
      const transactions = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate?.() || new Date(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));
      setSharedTransactions(transactions);
    });

    // Subscribe to shared budgets
    const budgetRef = collection(db, 'families', familyId, 'sharedBudgets');
    const unsubBudget = onSnapshot(query(budgetRef), (snapshot) => {
      const budgets = {};
      snapshot.docs.forEach(doc => {
        budgets[doc.data().category] = doc.data().amount;
      });
      setSharedBudgets(budgets);
    });

    // Subscribe to pending invites for this family
    const inviteRef = query(
      collection(db, 'familyInvites'),
      where('familyId', '==', familyId),
      where('status', '==', 'pending')
    );
    const unsubInvites = onSnapshot(inviteRef, (snapshot) => {
      setPendingInvites(snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })));
    });

    // Subscribe to family notifications (for in-app alerts)
    const notifRef = collection(db, 'families', familyId, 'notifications');
    const notifQuery = query(notifRef, orderBy('createdAt', 'desc'));
    const unsubNotifs = onSnapshot(notifQuery, (snapshot) => {
      const notifs = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
        createdAt: docSnap.data().createdAt?.toDate?.() || new Date()
      }));
      setFamilyNotifications(notifs);
    });
    
    newUnsubscribers.push(unsubTrans, unsubBudget, unsubInvites, unsubNotifs);
    setUnsubscribers(newUnsubscribers);
  };

  const fetchMemberDetails = async (familyData) => {
    // Prefer embedded memberMeta if present to avoid cross-user read rules
    if (familyData.memberMeta) {
      const entries = Object.entries(familyData.memberMeta);
      return entries.map(([memberId, meta]) => ({
        id: memberId,
        displayName: meta.displayName || 'Unknown',
        email: meta.email || '',
        photoURL: meta.photoURL || null,
        currency: meta.currency || 'INR',
        role:
          memberId === familyData.ownerId
            ? ROLES.OWNER
            : familyData.memberRoles?.[memberId] || meta.role || ROLES.MEMBER,
      }));
    }

    // Fallback: query each user's profile (requires Firestore rules to allow cross-user reads)
    const memberIds = [familyData.ownerId, ...(familyData.memberIds || [])];
    const memberDetails = [];

    for (const memberId of memberIds) {
      try {
        const userDoc = await getDoc(doc(db, 'users', memberId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          // Try to get the member's currency preference
          let memberCurrency = 'INR';
          try {
            const currencyDoc = await getDoc(doc(db, 'users', memberId, 'settings', 'currency'));
            if (currencyDoc.exists()) {
              memberCurrency = currencyDoc.data().displayCurrency || 'INR';
            }
          } catch (e) {
            console.log('Could not fetch member currency, using default');
          }
          
          memberDetails.push({
            id: memberId,
            displayName: userData.displayName || 'Unknown',
            email: userData.email,
            photoURL: userData.photoURL,
            currency: memberCurrency,
            role: memberId === familyData.ownerId 
              ? ROLES.OWNER 
              : (familyData.memberRoles?.[memberId] || ROLES.MEMBER)
          });
        }
      } catch (error) {
        console.error('Error fetching member:', error);
      }
    }

    return memberDetails;
  };

  // Create a new family
  const createFamily = async (familyName) => {
    if (!currentUser) throw new Error('No user logged in');

    try {
      const familyRef = await addDoc(collection(db, 'families'), {
        name: familyName,
        ownerId: currentUser.uid,
        memberIds: [],
        memberRoles: {},
        memberMeta: {
          [currentUser.uid]: {
            displayName: currentUser.displayName || 'Unknown',
            email: currentUser.email,
            photoURL: currentUser.photoURL,
            role: ROLES.OWNER,
          },
        },
        createdAt: serverTimestamp()
      });

      // Subscribe to the newly created family
      subscribeToFamily(familyRef.id);
      
      return familyRef.id;
    } catch (error) {
      console.error('Error creating family:', error);
      throw error;
    }
  };

  // Invite a member by email
  const inviteMember = async (email, role = ROLES.MEMBER) => {
    if (!currentUser || !family) throw new Error('No family to invite to');
    if (userRole !== ROLES.OWNER && userRole !== ROLES.ADMIN) {
      throw new Error('Only owners and admins can invite members');
    }

    try {
      // Check if already a member
      const existingMember = members.find(m => m.email === email);
      if (existingMember) {
        throw new Error('User is already a member');
      }

      // Check if already invited
      const existingInvite = pendingInvites.find(i => i.email === email);
      if (existingInvite) {
        throw new Error('User already has a pending invite');
      }

      await addDoc(collection(db, 'familyInvites'), {
        familyId: family.id,
        familyName: family.name,
        email: email.toLowerCase(),
        role: role,
        invitedBy: currentUser.uid,
        invitedByName: currentUser.displayName,
        status: 'pending',
        createdAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error inviting member:', error);
      throw error;
    }
  };

  // Accept an invitation
  const acceptInvite = async (inviteId) => {
    if (!currentUser) throw new Error('No user logged in');

    try {
      const inviteRef = doc(db, 'familyInvites', inviteId);
      const inviteDoc = await getDoc(inviteRef);
      
      if (!inviteDoc.exists()) throw new Error('Invite not found');
      
      const invite = inviteDoc.data();
      const familyRef = doc(db, 'families', invite.familyId);

      // Add user to family and store basic profile in memberMeta
      await updateDoc(familyRef, {
        memberIds: arrayUnion(currentUser.uid),
        [`memberRoles.${currentUser.uid}`]: invite.role,
        [`memberMeta.${currentUser.uid}`]: {
          displayName: currentUser.displayName || 'Unknown',
          email: currentUser.email,
          photoURL: currentUser.photoURL,
          role: invite.role,
        },
      });

      // Update invite status
      await updateDoc(inviteRef, {
        status: 'accepted',
        acceptedAt: serverTimestamp()
      });

      // Subscribe to the family
      subscribeToFamily(invite.familyId);
    } catch (error) {
      console.error('Error accepting invite:', error);
      throw error;
    }
  };

  // Decline an invitation
  const declineInvite = async (inviteId) => {
    if (!currentUser) throw new Error('No user logged in');

    try {
      const inviteRef = doc(db, 'familyInvites', inviteId);
      await updateDoc(inviteRef, {
        status: 'declined',
        declinedAt: serverTimestamp()
      });
      
      setPendingInvites(prev => prev.filter(i => i.id !== inviteId));
    } catch (error) {
      console.error('Error declining invite:', error);
      throw error;
    }
  };

  // Remove a member
  const removeMember = async (memberId) => {
    if (!currentUser || !family) throw new Error('No family');
    if (userRole !== ROLES.OWNER) throw new Error('Only owner can remove members');
    if (memberId === family.ownerId) throw new Error('Cannot remove owner');

    try {
      const familyRef = doc(db, 'families', family.id);
      await updateDoc(familyRef, {
        memberIds: arrayRemove(memberId),
        [`memberRoles.${memberId}`]: null
      });
    } catch (error) {
      console.error('Error removing member:', error);
      throw error;
    }
  };

  // Update member role
  const updateMemberRole = async (memberId, newRole) => {
    if (!currentUser || !family) throw new Error('No family');
    if (userRole !== ROLES.OWNER) throw new Error('Only owner can change roles');
    if (memberId === family.ownerId) throw new Error('Cannot change owner role');

    try {
      const familyRef = doc(db, 'families', family.id);
      await updateDoc(familyRef, {
        [`memberRoles.${memberId}`]: newRole
      });
    } catch (error) {
      console.error('Error updating role:', error);
      throw error;
    }
  };

  // Add shared transaction
  const addSharedTransaction = async (transactionData) => {
    if (!currentUser || !family) throw new Error('No family');

    try {
      const transRef = collection(db, 'families', family.id, 'sharedTransactions');
      const txDoc = await addDoc(transRef, {
        ...transactionData,
        userId: currentUser.uid,
        userName: currentUser.displayName,
        userPhoto: currentUser.photoURL,
        createdAt: serverTimestamp()
      });

      // Create a notification for all family members
      try {
        const notifRef = collection(db, 'families', family.id, 'notifications');
        const amount = transactionData.amount;
        const typeLabel = transactionData.type === 'income' ? 'income' : 'expense';
        const message = `${currentUser.displayName || 'Someone'} added a ${typeLabel} of ${amount} in ${transactionData.category}`;

        await addDoc(notifRef, {
          type: 'shared_transaction',
          transactionId: txDoc.id,
          userId: currentUser.uid,
          userName: currentUser.displayName,
          message,
          createdAt: serverTimestamp()
        });
      } catch (notifError) {
        console.error('Error creating family notification:', notifError);
      }
    } catch (error) {
      console.error('Error adding shared transaction:', error);
      throw error;
    }
  };

  // Set shared budget
  const setSharedBudget = async (category, amount) => {
    if (!currentUser || !family) throw new Error('No family');
    if (userRole === ROLES.MEMBER) throw new Error('Members cannot set budgets');

    try {
      const budgetRef = doc(db, 'families', family.id, 'sharedBudgets', category);
      await updateDoc(budgetRef, { category, amount }).catch(async () => {
        // If document doesn't exist, create it
        await addDoc(collection(db, 'families', family.id, 'sharedBudgets'), {
          category,
          amount,
          createdAt: serverTimestamp()
        });
      });
    } catch (error) {
      console.error('Error setting shared budget:', error);
      throw error;
    }
  };

  // Leave family
  const leaveFamily = async () => {
    if (!currentUser || !family) throw new Error('No family');
    if (userRole === ROLES.OWNER) throw new Error('Owner cannot leave. Transfer ownership or delete family.');

    try {
      const familyRef = doc(db, 'families', family.id);
      await updateDoc(familyRef, {
        memberIds: arrayRemove(currentUser.uid),
        [`memberRoles.${currentUser.uid}`]: null
      });
      
      setFamily(null);
      setMembers([]);
      setUserRole(null);
    } catch (error) {
      console.error('Error leaving family:', error);
      throw error;
    }
  };

  // Update family settings (including currency)
  const updateFamilySettings = async (newSettings) => {
    if (!currentUser || !family) throw new Error('No family');
    if (userRole === ROLES.MEMBER) throw new Error('Members cannot change settings');

    try {
      const familyRef = doc(db, 'families', family.id);
      await updateDoc(familyRef, {
        settings: { ...familySettings, ...newSettings }
      });
      setFamilySettings(prev => ({ ...prev, ...newSettings }));
    } catch (error) {
      console.error('Error updating family settings:', error);
      throw error;
    }
  };

  // Delete family (owner only)
  const deleteFamily = async () => {
    if (!currentUser || !family) throw new Error('No family');
    if (userRole !== ROLES.OWNER) throw new Error('Only owner can delete family');

    try {
      await deleteDoc(doc(db, 'families', family.id));
      setFamily(null);
      setMembers([]);
      setUserRole(null);
    } catch (error) {
      console.error('Error deleting family:', error);
      throw error;
    }
  };

  // Rename family (owner only)
  const renameFamily = async (newName) => {
    if (!currentUser || !family) throw new Error('No family');
    if (userRole !== ROLES.OWNER) throw new Error('Only owner can rename family');

    const trimmed = (newName || '').trim();
    if (!trimmed) throw new Error('Family name cannot be empty');

    try {
      const familyRef = doc(db, 'families', family.id);
      await updateDoc(familyRef, {
        name: trimmed,
        updatedAt: serverTimestamp()
      });
      setFamily(prev => (prev ? { ...prev, name: trimmed } : prev));
    } catch (error) {
      console.error('Error renaming family:', error);
      throw error;
    }
  };

  const value = {
    family,
    members,
    pendingInvites,
    sharedTransactions,
    sharedBudgets,
    familyNotifications,
    familySettings,
    loading,
    userRole,
    isOwner: userRole === ROLES.OWNER,
    isAdmin: userRole === ROLES.ADMIN || userRole === ROLES.OWNER,
    isMember: !!family,
    createFamily,
    inviteMember,
    acceptInvite,
    declineInvite,
    removeMember,
    updateMemberRole,
    addSharedTransaction,
    setSharedBudget,
    updateFamilySettings,
    leaveFamily,
    deleteFamily,
    renameFamily,
    ROLES,
    CURRENCY_DISPLAY_MODES
  };

  return (
    <FamilyContext.Provider value={value}>
      {children}
    </FamilyContext.Provider>
  );
};
